import * as R from 'remeda';
import { BboxParams } from '../../../../lib/spaceConvertor';
import { isBboxVisible } from '../../../../redux/modules/schema/helpers';
import { Grid } from '../../../../types/datapoints';
import { AnyDatapointSchema } from '../../../../types/schema';

type GridPosition = { page: number; x: number; y: number };

export type GridBboxPosition = {
  page: number;
  position: BboxParams;
};

/**
 * Columns of field types that never render a bbox on the document can still hold
 * a position, but there is nothing visible there to scroll to. Only columns whose
 * bbox is actually rendered may define the scroll target.
 */
const columnsWithVisibleBbox = (
  columns: Grid['columns'],
  schemaMap: ReadonlyMap<string, AnyDatapointSchema>
) =>
  columns.filter(column =>
    isBboxVisible(column.schemaId ? schemaMap.get(column.schemaId) : undefined)
  );

/**
 * Topmost-leftmost position of a multivalue's grid, used to scroll the document
 * to the table. Returns `undefined` when no part of the grid renders a bbox —
 * in that case the viewport is left where the user has it.
 */
export const getGridScrollPosition = (
  grid: { parts: Array<Grid> },
  schemaMap: ReadonlyMap<string, AnyDatapointSchema>
): GridBboxPosition | undefined => {
  const gridPosition = grid.parts.reduce<GridPosition | undefined>(
    (acc, curr) => {
      const columns = columnsWithVisibleBbox(curr.columns, schemaMap);

      if (columns.length === 0) {
        return acc;
      }

      const currentGridPosition = {
        page: curr.page,
        x: R.firstBy(columns, column => column.leftPosition)?.leftPosition ?? 0,
        y: R.firstBy(curr.rows, row => row.topPosition)?.topPosition ?? 0,
      };

      if (acc === undefined) {
        return currentGridPosition;
      }

      if (currentGridPosition.page < acc.page) {
        return currentGridPosition;
      }

      if (
        currentGridPosition.page === acc.page &&
        currentGridPosition.y < acc.y
      ) {
        return currentGridPosition;
      }

      if (
        currentGridPosition.page === acc.page &&
        currentGridPosition.y === acc.y &&
        currentGridPosition.x < acc.x
      ) {
        return currentGridPosition;
      }

      return acc;
    },
    undefined
  );

  return gridPosition
    ? {
        page: gridPosition.page,
        position: [
          gridPosition.x,
          gridPosition.y,
          gridPosition.x,
          gridPosition.y,
        ],
      }
    : undefined;
};
